import { useContext, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import { ListResponse, RootFolderListResponse, Video } from "vimeo";

import { Spinner } from "@salesforce/design-system-react";

import { Button, Notification } from "@vimeo/iris/components";

import { client as vimeoClient } from "../vimeoClient";
import { UtilityIcon } from "../components/icons/UtilityIcon";
import { EmbedInEmail } from "../views/EmbedInEmail/EmbedInEmail";
import { rootFolderVideoFields, videoFields } from "../fields";
import EmbedInEmailContext from "../contexts/embedInEmailContext";
import { getContentBlockType, isContentBlockContext } from "../sessionStorage";
import {
    appOrigin,
    getCloudPagesEmbedHtml,
    getCloudPagesSuperContent,
    getVideoIdFromUri,
} from "../common";

import "./videos.scoped.scss";
import classNames from "classnames";

import {
    embedInEmailAction,
    VideoActionsMenu,
} from "../components/VideoActionsMenu";
import {
    EmbedInEmailSource,
    EventName,
    sendAnalyticsEvent,
} from "../analytics/common";

const months = [
    "January",
    "February",
    "March",
    "April",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

/**
 * Returns the date formatted as MMM DD, YYYY.
 * @param d The ISO date string.
 */
function getFormattedDate(d: string): string {
    const date = new Date(d);
    return `${
        months[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()}`;
}

interface Props {
    selectedFolder: string;
    searchInput: string;
    onSetNumRootFolderVideos: (numVideos: number) => void;
}

export function Videos(props: Props) {
    const [embedInEmailContextState, setEmbedInEmailContextState] =
        useContext(EmbedInEmailContext);
    const [videos, setVideos] = useState<Video[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [showEmbedInEmailModal, setShowEmbedInEmailModal] = useState(false);
    const [
        showCannotEmbedPrivateVideoNotification,
        setShowCannotEmbedPrivateVideoNotification,
    ] = useState(false);
    const navigate = useNavigate();

    const { selectedFolder, searchInput, onSetNumRootFolderVideos } = props;

    async function getFolderVideos(pageParam = 1) {
        if (!selectedFolder) {
            return;
        }

        let query = "";
        if (searchInput) {
            query = `query=${searchInput}`;
        }

        if (!selectedFolder.includes("root")) {
            const videosResp = await vimeoClient.get<ListResponse<Video>>(
                `/api/vimeo${selectedFolder}/videos?page=${pageParam}&fields=${videoFields}&${query}&sort=last_user_action_event_date&direction=desc`
            );
            if (pageParam === 1) {
                setVideos(videosResp.data.data);
            } else {
                setVideos([...videos, ...videosResp.data.data]);
            }

            setHasNextPage(Boolean(videosResp.data?.paging?.next));

            // Reset the count of the videos in the root folder so that the state actually
            // triggers a change when the user navigates to the root folder again and
            // we set the count again.
            onSetNumRootFolderVideos(0);
        } else {
            const videosResp = await vimeoClient.get<RootFolderListResponse>(
                `/api/vimeo${selectedFolder}?page=${pageParam}&fields=${rootFolderVideoFields}&${query}&filter=video&sort=last_user_action_event_date&direction=desc`
            );

            const rootFolderVideos = videosResp.data.data.filter(
                (d) => d.type === "video"
            );
            setHasNextPage(Boolean(videosResp.data?.paging?.next));

            if (!rootFolderVideos) {
                setVideos([]);
                onSetNumRootFolderVideos(0);
                return;
            }

            const videosOnly = rootFolderVideos.map((v) => v.video!);
            setVideos(videosOnly);
            onSetNumRootFolderVideos(videosOnly.length);
        }
    }

    useEffect(() => {
        setPage(1);

        setLoading(true);
        getFolderVideos()
            .catch(console.error)
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolder, searchInput]);

    async function onVisibilityChange() {
        if (document.visibilityState !== "visible") {
            return;
        }

        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        setLoading(true);
        await getFolderVideos();
        setLoading(false);
    }

    async function onMessage(data: MessageEvent) {
        if (data.origin !== appOrigin) {
            return;
        }

        setLoading(true);
        await getFolderVideos();
        setLoading(false);
    }

    function onVideoTrimmerOpened(opened: Window) {
        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        if (!opened) {
            return;
        }

        opened.onmessage = onMessage;
    }

    async function loadMore() {
        setLoading(true);
        await getFolderVideos(page + 1);
        setLoading(false);
        setPage(page + 1);
    }

    const contentBlockType = getContentBlockType();

    async function onEmbedButtonClicked(video: Video) {
        const videoId = getVideoIdFromUri(video.uri);

        switch (contentBlockType) {
            case "email":
                setEmbedInEmailContextState({
                    video: video,
                });

                sendAnalyticsEvent(EventName.openEmbedInEmail, {
                    path: null,
                    is_my_videos: null,
                    clip_id: videoId,
                    location: "embed_cta",
                }, 3);
                navigate(`${video.uri}/${embedInEmailAction}`);
                break;
            case "web":
                if (video.privacy.view === "nobody") {
                    setShowCannotEmbedPrivateVideoNotification(true);
                    return;
                }
                // Set the block's content to this video's HTML
                // and save the video ID so we can restore state
                // when the user selects the block in the content
                // area again.
                window.blockSdk.setContent(
                    await getCloudPagesEmbedHtml(
                        video.uri,
                        video.player_embed_url
                    ),
                    () => {
                        sendAnalyticsEvent(EventName.embed, {
                            name: "success_embed",
                            asset_type: "video",
                            location: "embed_cta",
                            video_id: videoId,
                            cta: "embed",
                        });
                    }
                );
                // The super content is the HTML that is only meant
                // for preview. It doesn't end up in the actual markup
                // for the block. Meaning it won't be in the published
                // version of the cloud page. It's purely for rendering
                // the preview stage.
                window.blockSdk.setSuperContent(
                    getCloudPagesSuperContent(video)
                );
                window.blockSdk.setData(
                    { web: { videoUri: video.uri } },
                    () => {}
                );
                navigate(`${video.uri}/settings/embed-for-web`);
                break;
        }
    }

    const videoEls = videos?.map((v, idx) => {
        const canEdit = v.metadata?.interactions?.edit;

        return (
            <div
                key={idx}
                className="slds-col"
                style={{
                    paddingTop: "0.75rem",
                    paddingBottom: "0.75rem",
                }}
            >
                {isContentBlockContext() && (
                    <div className="embed-button-container">
                        <Button
                            className="embed-button"
                            format="primary"
                            onClick={() => onEmbedButtonClicked(v)}
                        >
                            Embed
                        </Button>
                    </div>
                )}
                <Link to={`${v.uri}/settings/basic`} className={canEdit ? undefined: 'disabled'}>
                    <img
                        className="video-splash"
                        src={`${v.pictures.base_link}_640x360?r=pad`}
                        alt={v.name}
                    />
                </Link>
                <div className="video-metadata-container slds-var-m-top_small">
                    <div
                        style={{
                            display: "flex",
                            flexFlow: "row nowrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexFlow: "column nowrap",
                            }}
                            title={v.name}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexFlow: "row nowrap",
                                }}
                            >
                                {v.privacy.view !== "anybody" && (
                                    <span className="video-privacy-icon slds-icon_container slds-current-color">
                                        <UtilityIcon
                                            iconName="lock"
                                            svgClass="slds-icon slds-icon_x-small"
                                        />
                                    </span>
                                )}
                                <Link
                                    className={classNames(["video-title", {'disabled': !canEdit}])}
                                    style={{ color: "#23313B" }}
                                    to={`${v.uri}/settings/basic`}
                                >
                                    <div>{v.name}</div>
                                </Link>
                            </div>
                            <span style={{ color: "#3F5465" }}>
                                {getFormattedDate(v.created_time)}
                            </span>
                        </div>
                        <VideoActionsMenu
                            video={v}
                            onVideoTrimmerOpened={onVideoTrimmerOpened}
                            onShowEmbedInEmailModal={() =>
                                setShowEmbedInEmailModal(true)
                            }
                        />
                    </div>
                </div>
            </div>
        );
    });

    const emptyState = (
        <div className="slds-text-heading_small slds-align_absolute-center">
            There are no videos in this folder.
        </div>
    );

    return (
        <div>
            {loading && <Spinner />}
            <div className="videos-grid">{videoEls}</div>
            {hasNextPage && (
                <Button
                    className="load-more"
                    format="secondary"
                    onClick={loadMore}
                >
                    Load more
                </Button>
            )}
            {!videoEls?.length && emptyState}
            {embedInEmailContextState.video && showEmbedInEmailModal && (
                <EmbedInEmail
                    source={EmbedInEmailSource.appHome}
                    useModal={true}
                    show={showEmbedInEmailModal}
                    onClose={() => setShowEmbedInEmailModal(false)}
                />
            )}
            <Notification
                content="Video is Private. Change its privacy to embed it."
                showing={showCannotEmbedPrivateVideoNotification}
                automatic={true}
                onComplete={() =>
                    setShowCannotEmbedPrivateVideoNotification(false)
                }
            ></Notification>
        </div>
    );
}
